/* eslint-disable jsx-a11y/anchor-is-valid */
import * as classnames from 'classnames'
import * as React from 'react'
import debounce from 'lodash-es/debounce'
import get from 'lodash-es/get'
import Scrollbar from 'react-scrollbars-custom'
import { Site } from '@envato/foundation-types'

import { config } from '../../../../common/config'
import SearchLink from '../../../containers/searchLinkContainer'
import ArrowLeftIcon from '../../icons/carouselArrowLeft'
import ArrowRightIcon from '../../icons/carouselArrowRight'
import { ICategoryPageBlockComponent } from '../../../../typings/pageBlocks'
import SVGSpriteWrapper from '../../svgSpriteWrapper'

import { IFlatQuery } from '../../../../common/utils/location/types'

import * as styles from './categoryIconsBlock.module.css'

interface ICategoryWithIconBlockData {
  name: string
  count: number
  urlSearchParams: IFlatQuery
}

interface IProps extends ICategoryPageBlockComponent {
  data: ICategoryWithIconBlockData[]
}

const scrollbarStyles: React.CSSProperties = {
  position: 'absolute',
  overflow: 'hidden',
  borderRadius: 4,
  background: 'rgba(0, 0, 0, 0.1)',
  userSelect: 'none',
  height: 5,
  width: '100%',
  bottom: 0,
  left: 0
}

const getCategoryIcon = (
  currentSite: Site,
  category: ICategoryWithIconBlockData,
  backgroundColor: string
) => {
  const categoryPath = get(category, 'urlSearchParams.category', '')
  const categoryNameFromPath = (categoryPath
    .split('/')
    .pop() as string).toLowerCase()

  return SVGSpriteWrapper.SVG({
    iconId: `#${currentSite}-svg-${categoryNameFromPath}`,
    fallbackId: `#${currentSite}-svg-generic`,
    backgroundColor
  })
}

interface IState {
  currentScrollLeft: number
  hideCarousel: boolean
}

enum CarouselDirection {
  RIGHT = 'RIGHT',
  LEFT = 'LEFT'
}

const iconBackgrounds = [
  '#d2fae6',
  '#beebf6',
  '#d5d5ff',
  '#ffdbcd',
  '#ffd6b0',
  '#fff48a'
]

class CategoryIconsBlock extends React.Component<IProps, IState> {
  private sliderRef = React.createRef<HTMLDivElement>()
  private sliderWrapperRef = React.createRef<HTMLDivElement>()
  private itemsPerScroll = 3
  private onResize = () => {}

  private scrollToDirection = (direction: CarouselDirection) => {
    const el = this.sliderRef.current

    if (el) {
      const children = el.querySelectorAll('section')
      const nextEl = this.findNextElementToFocus(children, direction)
      const leftGutter = (window.innerWidth - el.clientWidth) / 2
      const nextElLeft = nextEl.getBoundingClientRect().left - leftGutter
      const nextScrollX = (el.scrollLeft || 0) + nextElLeft

      el.scrollTo({ left: nextScrollX, behavior: 'smooth' })
    }

    return null
  }

  private findNextElementToFocus(
    elements: NodeListOf<HTMLElement>,
    direction: CarouselDirection
  ) {
    const right = direction === CarouselDirection.RIGHT
    let nextIndex = right ? elements.length - 1 : 0
    let visibleItemsCount = 0
    let firstVisibleElement = NaN

    for (let i = 0, l = elements.length; i < l; i++) {
      if (this.isElementInViewport(elements[i])) {
        if (Number.isNaN(firstVisibleElement)) {
          firstVisibleElement = i
        }

        visibleItemsCount++
      }
    }

    const itemsToScroll =
      visibleItemsCount < this.itemsPerScroll
        ? visibleItemsCount
        : this.itemsPerScroll

    const tempNextIndex = right
      ? firstVisibleElement + itemsToScroll
      : firstVisibleElement - itemsToScroll

    if (
      (right && tempNextIndex < elements.length) ||
      (!right && tempNextIndex > 0)
    ) {
      nextIndex = tempNextIndex
    }

    return elements[nextIndex]
  }

  private isElementInViewport(el: HTMLElement) {
    const slider = this.sliderRef.current

    if (slider) {
      const rect = el.getBoundingClientRect()
      const gutter = Math.floor((window.innerWidth - slider.clientWidth) / 2)

      return (
        rect.left - gutter >= 0 &&
        rect.right + gutter <=
          (window.innerWidth || document.documentElement.clientWidth)
      )
    }

    return false
  }

  private isArrowDisabled(position: CarouselDirection) {
    const { currentScrollLeft } = this.state
    const el = this.sliderRef.current

    if (position === CarouselDirection.LEFT) {
      return currentScrollLeft <= 0
    }

    return el && el.scrollWidth <= currentScrollLeft + el.clientWidth
  }

  private onScroll = debounce(() => {
    const el = this.sliderRef.current

    if (el) {
      this.setState({ currentScrollLeft: el.scrollLeft })
    }
  }, 100)

  public componentDidMount() {
    if (this.sliderWrapperRef.current && this.sliderRef.current) {
      this.sliderWrapperRef.current.style.height = `
        ${this.sliderRef.current.scrollHeight + 10}px
      `
    }

    this.onResize = debounce(() => {
      const el = this.sliderRef.current

      if (el) {
        this.setState({
          currentScrollLeft: el.scrollLeft,
          hideCarousel: el.scrollWidth === el.clientWidth
        })
      }
    }, 100)

    window.addEventListener('resize', this.onResize)
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.onResize)
  }

  public constructor(props: IProps) {
    super(props)

    this.state = {
      currentScrollLeft: 0,
      hideCarousel: false
    }
  }

  public render() {
    const { data, currentSite } = this.props
    const { hideCarousel } = this.state
    const arrowLeft = classnames(styles.arrow, styles.arrowLeft, {
      [styles.isDisabled]: this.isArrowDisabled(CarouselDirection.LEFT)
    })
    const arrowRight = classnames(styles.arrow, styles.arrowRight, {
      [styles.isDisabled]: this.isArrowDisabled(CarouselDirection.RIGHT)
    })

    return (
      <nav>
        <SVGSpriteWrapper
          spriteURL={`${config.assets.PUBLIC_PATH}/images/categories/sprites-${currentSite}-v3.svg`}
        >
          <Scrollbar
            onScroll={this.onScroll}
            style={{ width: '100%', height: 160 }}
            renderer={props => {
              const { elementRef, ...restProps } = props

              const setRef = (current: HTMLDivElement) => {
                this.sliderWrapperRef = { current }
                elementRef && elementRef(current)
              }

              return <div {...restProps} ref={setRef} />
            }}
            contentProps={{
              renderer: props => {
                const { elementRef, ...restProps } = props
                return (
                  <span
                    {...restProps}
                    ref={elementRef}
                    className={styles.categoryList}
                  />
                )
              }
            }}
            scrollerProps={{
              renderer: props => {
                const { elementRef, ...restProps } = props

                const setRef = (current: HTMLDivElement) => {
                  this.sliderRef = { current }
                  elementRef && elementRef(current)
                }

                return <span {...restProps} ref={setRef} />
              }
            }}
            trackXProps={{
              renderer: props => {
                const { elementRef, ...restProps } = props
                return (
                  <span
                    {...restProps}
                    ref={elementRef}
                    style={scrollbarStyles}
                  />
                )
              }
            }}
          >
            {data.map((category: ICategoryWithIconBlockData, index: number) => {
              const colorIndex = index % iconBackgrounds.length

              return (
                <section key={index} className={styles.categoryItem}>
                  <SearchLink
                    locationParams={category.urlSearchParams}
                    className={styles.categoryItemHeader}
                  >
                    {getCategoryIcon(
                      currentSite,
                      category,
                      iconBackgrounds[colorIndex]
                    )}
                    <h3 className={styles.categoryItemName}>{category.name}</h3>
                  </SearchLink>
                </section>
              )
            })}
          </Scrollbar>

          {!hideCarousel && (
            <React.Fragment>
              <ArrowLeftIcon
                className={arrowLeft}
                onClick={() => this.scrollToDirection(CarouselDirection.LEFT)}
              />
              <ArrowRightIcon
                className={arrowRight}
                onClick={() => this.scrollToDirection(CarouselDirection.RIGHT)}
              />
            </React.Fragment>
          )}
        </SVGSpriteWrapper>
      </nav>
    )
  }
}

export default CategoryIconsBlock
